import moment from 'moment';

import { BookingDiaryEventModel } from './typings/booking-diary.event';
import { statusCodeFor }          from '../transaction-center/jobs/transaction-center.jobs.status-tone';

// a drag ends with a pointer-up the browser also reports as a click, so clicks are ignored briefly
export const CLICK_SUPPRESS_MS : number                            = 300;
export const DATE_FORMAT       : string                            = 'YYYY-MM-DD';
export const DRAG_START_DELAY  : { mouse : number; touch : number } = { mouse: 0, touch: 150 };
export const MIN_HOURS         : number                            = 0.25;
// unassigned cards the rail is deep before it scrolls, and past which it points to the list
export const RAIL_LIMIT        : number                            = 10;
export const SLOT_MINUTES      : number                            = 15;

// the backlog endpoint allows 500 rows and one more is asked for, so the whole backlog is one under
export const UNASSIGNED_ALL_SIZE : number = 499;

const FINALISED_STATUS : string = 'F';

// lays overlapping bookings into side-by-side lanes and stamps each with the lane count
export function assignLanes (
  events : Array<BookingDiaryEventModel>
) : Array<BookingDiaryEventModel> {
  const sorted = events
    .slice()
    .sort(( a, b ) => a.startMinutes - b.startMinutes || a.endMinutes - b.endMinutes);
  const lanes : Array<number> = [];

  sorted.forEach(event => {
    let lane = lanes.findIndex(end => end <= event.startMinutes);

    if ( lane === -1 ) {
      lane = lanes.length;
    }

    lanes[lane] = event.endMinutes;
    event.lane  = lane;
  });

  sorted.forEach(event => {
    event.lanes = lanes.length;
  });

  return sorted;
}

// a split adds a part to the labour line, which a finalised job no longer takes
export function canSplitEvent ( event : BookingDiaryEventModel ) : boolean {
  return event.kind === 'booking'
    && !!event.eventItemId
    && statusCodeFor(event.jobStatus) !== FINALISED_STATUS;
}

// Unassigned lists a started job's parts only until its job card is finalised, so one dropped there would vanish
export function canUnassignEvent ( event : BookingDiaryEventModel ) : boolean {
  return event.kind === 'booking'
    && statusCodeFor(event.jobStatus) !== FINALISED_STATUS;
}

export function clampMinutes ( minutes : number, min : number, max : number ) : number {
  return Math.min(Math.max(minutes, min), Math.max(min, max));
}

export function formatClockLabel ( minutes : number ) : string {
  const hours  = Math.floor(minutes / 60) % 24;
  const mins   = minutes % 60;
  const suffix = hours >= 12
    ? 'PM'
    : 'AM';

  return `${ hours % 12 || 12 }:${ mins < 10 ? '0' + mins : mins } ${ suffix }`;
}

function pad ( value : number ) : string {
  return value < 10
    ? `0${ value }`
    : `${ value }`;
}

export function formatMinutes ( minutes : number ) : string {
  const hours = Math.floor(minutes / 60);
  const mins  = minutes % 60;

  return `${ pad(hours) }:${ pad(mins) }:00`;
}

// the server reads an event time's offset as today's and moves it across a clock change itself
export function eventTimeLocal ( date : string, minutes : number ) : string {
  return `${ date }T${ formatMinutes(minutes) }${ moment().format('Z') }`;
}

export function formatMinutesLabel ( minutes : number ) : string {
  const hours  = Math.floor(minutes / 60) % 24;
  const suffix = hours >= 12
    ? 'PM'
    : 'AM';

  return `${ hours % 12 || 12 } ${ suffix }`;
}

// a slot is gone once its first quarter hour is behind the clock, on today or any earlier day
export function hasPassed ( date : string, startMinutes : number ) : boolean {
  const today      = moment().format(DATE_FORMAT);
  const minutesNow = moment().diff(moment().startOf('day'), 'minutes');

  return date < today || ( date === today && startMinutes + SLOT_MINUTES <= minutesNow );
}

// the zone suffix is ignored so a booking keeps the shop's wall-clock time in any browser timezone
export function minutesFromLocal ( datetime : string ) : number {
  const match = /T(\d{2}):(\d{2})/.exec(datetime || '');

  return match
    ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10)
    : 0;
}

export function minutesFromTime ( time : string ) : number {
  const match = /^(\d{1,2}):(\d{2})/.exec(time || '');

  return match
    ? parseInt(match[1], 10) * 60 + parseInt(match[2], 10)
    : 0;
}

// the Monday-first week rows that cover a month
function monthWeeksFor ( date : string ) : Array<Array<string>> {
  const first = moment(date, DATE_FORMAT).startOf('month');
  const last  = moment(date, DATE_FORMAT).endOf('month');

  const cursor = first.clone().isoWeekday(1);
  const weeks : Array<Array<string>> = [];

  while ( cursor.isBefore(last) || cursor.isSame(last, 'day') ) {
    const week : Array<string> = [];

    for ( let day = 0; day < 7; day++ ) {
      week.push(cursor.format(DATE_FORMAT));

      cursor.add(1, 'days');
    }

    weeks.push(week);
  }

  return weeks;
}

// the week rows of a run of months, without repeating a week two months share
export function monthRangeWeeksFor ( date : string, months : number = 1 ) : Array<Array<string>> {
  let weeks = monthWeeksFor(date);

  for ( let index = 1; index < months; index++ ) {
    const lastWeek  = weeks[weeks.length - 1][0];
    const nextMonth = moment(date, DATE_FORMAT).add(index, 'month').format(DATE_FORMAT);

    weeks = weeks.concat(monthWeeksFor(nextMonth).filter(week => week[0] > lastWeek));
  }

  return weeks;
}

// an unavailable time is written back with the offset it already carries, never the browser's
export function offsetFromLocal ( datetime : string, fallback : string ) : string {
  const match = /([+-]\d{2}:?\d{2}|Z)$/.exec(datetime || '');

  return match
    ? match[1]
    : fallback;
}

export function snapMinutes ( minutes : number, step : number = SLOT_MINUTES ) : number {
  return Math.round(minutes / step) * step;
}

export function weekDatesFor ( date : string ) : Array<string> {
  const monday = moment(date, DATE_FORMAT).isoWeekday(1);

  const dates : Array<string> = [];

  for ( let day = 0; day < 7; day++ ) {
    dates.push(monday.clone().add(day, 'days').format(DATE_FORMAT));
  }

  return dates;
}
